/**
 * MicButton — voice-dictation button with two backends:
 *
 *   1. "whisper" (default) — record with MediaRecorder, POST the audio blob
 *      to /api/content-calendar/transcribe, replace/append the resulting
 *      text. Far more accurate than the browser's built-in speech API,
 *      handles South African accents and lodge-industry terminology
 *      (SANS 241, borehole, MBBR, etc.) correctly.
 *
 *   2. "browser" — uses window.SpeechRecognition for instant inline
 *      transcription, no backend round-trip. Less accurate. Kept as a
 *      fallback option for fields where speed beats accuracy.
 *
 * Props:
 *   · value, onChange      — the field state being dictated into
 *   · mode                 — "append" (default) | "replace"
 *   · backend              — "whisper" (default) | "browser"
 *   · apiToken             — required when backend === "whisper"
 *   · apiUrl               — base URL for the transcribe endpoint
 *   · lang                 — "en" by default (Whisper) / "en-ZA" (browser)
 *   · size, testid, title
 *
 * Recording UX:
 *   · click → request mic permission → start recording → pulsing red button
 *   · click again → stop recording → "transcribing…" state → text inserted
 *   · auto-stop after 60 seconds to avoid silent runaway recordings
 *
 * Concurrency guard: only one MicButton may record at a time (module-level
 * singleton). Clicking another mic while one is hot stops the previous.
 */
import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";

// ── Browser-API fallback (only loaded when backend === "browser") ──
const SR =
  typeof window !== "undefined" &&
  (window.SpeechRecognition || window.webkitSpeechRecognition);

// ── Module-level singleton: at most one active recorder/recogniser ──
let activeStopper = null;

const MAX_RECORDING_MS = 60 * 1000;     // 60 seconds — protects mic + tokens
const MIN_RECORDING_MS = 250;           // ignore accidental clicks

export function MicButton({
  value = "",
  onChange,
  mode = "append",
  backend = "whisper",
  apiToken,
  apiUrl = process.env.REACT_APP_BACKEND_URL,
  lang,
  size = 14,
  testid,
  title = "Dictate — voice to text (Whisper)",
}) {
  const [state, setState] = useState("idle");   // idle | recording | transcribing | error
  const [errMsg, setErrMsg] = useState("");
  const recorderRef = useRef(null);             // MediaRecorder | null
  const streamRef   = useRef(null);             // MediaStream  | null
  const chunksRef   = useRef([]);
  const timerRef    = useRef(null);
  const recogniserRef = useRef(null);           // browser fallback

  // Cleanup on unmount.
  useEffect(() => () => fullStop(), []);        // eslint-disable-line react-hooks/exhaustive-deps

  const fullStop = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch { /* noop */ }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (recogniserRef.current) {
      try { recogniserRef.current.stop(); } catch { /* noop */ }
      recogniserRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
    if (activeStopper === fullStop) activeStopper = null;
  };

  // ─────────────────────────────────────────── Whisper backend
  const startWhisper = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setErrMsg("Your browser does not expose a microphone.");
      setState("error");
      return;
    }

    if (activeStopper && activeStopper !== fullStop) {
      try { activeStopper(); } catch { /* noop */ }
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Pick the best mime type the browser supports.  Whisper accepts webm/mp4.
      const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ];
      const mimeType =
        candidates.find((t) => window.MediaRecorder?.isTypeSupported?.(t)) ||
        "";
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;
      chunksRef.current = [];
      const startedAt = Date.now();

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        // Always release the OS mic indicator promptly.
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        const blob = new Blob(chunksRef.current,
          { type: recorder.mimeType || "audio/webm" });
        chunksRef.current = [];

        if (Date.now() - startedAt < MIN_RECORDING_MS) {
          setState("idle"); return;          // accidental click
        }

        setState("transcribing");
        try {
          const form = new FormData();
          const ext =
            (recorder.mimeType || "").includes("mp4") ? "mp4" :
            (recorder.mimeType || "").includes("ogg") ? "ogg" :
            "webm";
          form.append("audio", blob, `voice.${ext}`);
          form.append("language", lang || "en");
          const r = await fetch(`${apiUrl}/api/content-calendar/transcribe`, {
            method: "POST",
            headers: { Authorization: `Bearer ${apiToken}` },
            body: form,
          });
          const data = await r.json();
          if (!r.ok) throw new Error(data.detail || "Transcription failed.");
          insertText(data.text || "");
          setState("idle");
        } catch (err) {
          setErrMsg(err.message);
          setState("error");
          setTimeout(() => setState("idle"), 2500);
        }
      };

      activeStopper = fullStop;
      recorder.start();
      setState("recording");
      // safety auto-stop
      timerRef.current = setTimeout(() => {
        if (recorderRef.current && recorderRef.current.state === "recording") {
          try { recorderRef.current.stop(); } catch { /* noop */ }
        }
      }, MAX_RECORDING_MS);
    } catch (err) {
      setErrMsg(err.message || "Microphone permission denied.");
      setState("error");
      setTimeout(() => setState("idle"), 2500);
    }
  };

  // ─────────────────────────────────────────── Browser SpeechRecognition (fallback)
  const startBrowser = () => {
    if (!SR) { setErrMsg("Browser speech API not available."); setState("error"); return; }
    if (activeStopper && activeStopper !== fullStop) {
      try { activeStopper(); } catch { /* noop */ }
    }
    const r = new SR();
    r.continuous = true;
    r.interimResults = true;
    r.lang = lang || "en-ZA";
    r.maxAlternatives = 1;
    const startValue = mode === "replace" ? "" : value || "";
    let finalChunk = "";
    r.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) finalChunk += res[0].transcript;
        else interim += res[0].transcript;
      }
      const merged = (finalChunk + interim).trim();
      onChange(
        mode === "replace"
          ? merged
          : startValue ? `${startValue} ${merged}`.replace(/\s+/g, " ").trimEnd() : merged
      );
    };
    r.onerror = () => fullStop();
    r.onend = () => { setState("idle"); fullStop(); };
    recogniserRef.current = r;
    activeStopper = fullStop;
    try {
      r.start(); setState("recording");
    } catch (e) {
      console.warn("[mic] start failed", e); fullStop();
    }
  };

  // ─────────────────────────────────────────── insert helper
  const insertText = (raw) => {
    const t = (raw || "").trim();
    if (!t) return;
    if (mode === "replace") {
      onChange(t);
    } else {
      onChange(value && value.trim()
        ? `${value.replace(/\s+$/, "")} ${t}`
        : t);
    }
  };

  // ─────────────────────────────────────────── click handler
  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (state === "recording") {
      if (backend === "whisper" && recorderRef.current && recorderRef.current.state === "recording") {
        try { recorderRef.current.stop(); } catch { /* noop */ }
      } else if (backend === "browser" && recogniserRef.current) {
        try { recogniserRef.current.stop(); } catch { /* noop */ }
      }
      return;
    }
    if (state === "transcribing") return;
    setErrMsg("");
    if (backend === "whisper") startWhisper();
    else startBrowser();
  };

  // ─────────────────────────────────────────── render
  const disabled =
    backend === "browser" && !SR ||
    backend === "whisper" && !apiToken;

  if (disabled) {
    return (
      <button
        type="button"
        title={
          backend === "browser"
            ? "Voice dictation isn't supported in this browser."
            : "Sign in first — dictation requires the admin token."
        }
        data-testid={testid ? `${testid}-unsupported` : undefined}
        disabled
        className="inline-flex items-center justify-center"
        style={{
          width: 26, height: 26,
          background: "transparent",
          border: "1px solid rgba(12,42,64,0.15)",
          color: "rgba(12,42,64,0.30)",
          cursor: "not-allowed",
          borderRadius: 3,
        }}
      >
        <MicOff size={size} />
      </button>
    );
  }

  let icon = <Mic size={size} />;
  let bg = "transparent";
  let bd = "rgba(12,42,64,0.30)";
  let fg = "#0C2A40";
  let ring = "none";
  let cursor = "pointer";

  if (state === "recording") {
    bg = "#B85C38"; bd = "#B85C38"; fg = "#FFFFFF";
    ring = "0 0 0 4px rgba(184,92,56,0.18)";
  } else if (state === "transcribing") {
    icon = <Loader2 size={size} className="animate-spin" />;
    bg = "#1FB6C1"; bd = "#1FB6C1"; fg = "#0C2A40";
    cursor = "wait";
  } else if (state === "error") {
    bg = "rgba(184,92,56,0.12)"; bd = "rgba(184,92,56,0.55)"; fg = "#B85C38";
  }

  return (
    <button
      type="button"
      onClick={toggle}
      title={
        state === "recording"   ? "Stop recording" :
        state === "transcribing" ? "Transcribing…" :
        state === "error"        ? (errMsg || "Try again") :
        title
      }
      data-testid={testid}
      className="inline-flex items-center justify-center"
      style={{
        width: 26, height: 26,
        background: bg,
        border: `1px solid ${bd}`,
        color: fg,
        cursor,
        borderRadius: 3,
        boxShadow: ring,
        transition: "all 120ms ease",
      }}
    >
      {icon}
    </button>
  );
}
